import { ReactNode, createContext, useContext, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { EnvironmentContext } from 'config/environment'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useGraphQLClient } from 'modules/core/lib/graphql/provider'
import trackTransition from 'modules/core/lib/routeTransitionTracking'

import { useDatoLocale } from 'modules/onboarding/hooks/useDatoLocale'
import usePricingLocation from 'modules/onboarding/hooks/usePricingLocation'
import { Pricing } from 'modules/onboarding/lib/pricing/types'

type DatoPlansData = {
  datoPlans: Pricing | null
  country: string
  locale: string
}

const DatoPlansContext = createContext<DatoPlansData>({
  datoPlans: null,
  country: '',
  locale: '',
})

type DatoPlansProviderProps = {
  children: ReactNode
}

const DatoPlansProvider = ({ children }: DatoPlansProviderProps) => {
  const { webpageLocation } = useContext(EnvironmentContext)
  const [datoPlans, setDatoPlans] = useState<Pricing | null>(null)
  const graphQLClient = useGraphQLClient()
  const { country } = usePricingLocation()
  const { locale } = useDatoLocale()
  const { search } = useLocation()
  const preview = new URLSearchParams(search).get('preview') === 'true'

  useUnsafeEffect(() => {
    fetch(`${webpageLocation}/api/pricing/${locale}-${country}?preview=${preview}`)
      .then((res) => res.json())
      .then((res) => {
        trackTransition(graphQLClient, 'payment_page_view')
        setDatoPlans(res)
      })
  }, [webpageLocation, country, locale, graphQLClient, preview])

  return (
    <DatoPlansContext.Provider value={{ datoPlans, country, locale }}>
      {children}
    </DatoPlansContext.Provider>
  )
}

export const useDatoPlans = () => useContext(DatoPlansContext)

export default DatoPlansProvider
